import { CircularProgress, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { trackEvent } from "src/Amplitude";

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100vh",
  textAlign: "center",
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.secondary.light,
  fontFamily: "Roobert",
}));

const Fallback = styled("div")({
  marginTop: "20px",
});

const Link = styled("a")(({ theme }) => ({
  color: theme.palette.secondary.main,
  textDecoration: "none",
  padding: "10px 20px",
  border: `1px solid ${theme.palette.secondary.main}`,
  borderRadius: "5px",
  display: "inline-block",
  marginTop: "10px",
  fontFamily: "Roobert",
  fontSize: "16px",
  fontWeight: 500,
  "&:hover": {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.primary.main,
  },
}));

const ErrorCard = styled("div")(({ theme }) => ({
  color: theme.palette.primary.main,
  fontFamily: "Roobert",
  fontSize: "18px",
  fontWeight: 500,
  borderRadius: "20px",
  width: "100%",
  textAlign: "center",
}));

const SpinnerWrapper = styled("div")({
  marginTop: "20px",
  marginBottom: "20px",
});

const StyledTypography = styled(Typography)(({ theme }) => ({
  fontFamily: "Roobert",
  fontSize: "14px",
  fontWeight: 500,
  marginBottom: "8px",
  color: theme.palette.secondary.light,
}));

const StyledErrorTypography = styled(Typography)(({ theme }) => ({
  fontFamily: "Roobert",
  fontSize: "18px",
  textTransform: "capitalize",
  fontWeight: 500,
  marginBottom: "8px",
  color: theme.palette.secondary.light,
}));

const WebsiteLink = styled("a")(({ theme }) => ({
  color: theme.palette.secondary.main,
  textDecoration: "none",
  padding: "10px 20px",
  border: `1px solid ${theme.palette.secondary.main}`,
  borderRadius: "5px",
  display: "inline-block",
  marginTop: "20px",
  fontFamily: "Roobert",
  fontSize: "16px",
  fontWeight: 500,
  "&:hover": {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.primary.main,
  },
}));

const VerifyEmail = () => {
  const location = useLocation();
  const [code, setCode] = React.useState("");
  const [error, setError] = React.useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get("code");
    const error = searchParams.get("error");

    if (error) {
      setError(true);
      trackEvent("IOS_EMAIL_VERIFICATION_ERROR", {
        error: error,
      });
    }

    if (code) {
      setCode(code);
      // Try different methods for auto-redirect
      const appUrl = `com.calebandbrown.app://verifyemail?code=${code}`;
      // Method 1: Direct location change
      window.location.href = appUrl;

      // Method 2: Window open
      window.open(appUrl, "_self");
    } else {
      setError(true);
      trackEvent("IOS_EMAIL_VERIFICATION_ERROR", {
        error: "No verification code provided",
      });
    }
  }, [location]);

  if (error) {
    return (
      <Container>
        <ErrorCard>
          <StyledErrorTypography>Something went wrong!</StyledErrorTypography>
          <StyledTypography>Please Contact Support</StyledTypography>
        </ErrorCard>
        <WebsiteLink href="https://calebandbrown.com/" target="_blank" rel="noopener noreferrer">
          Visit Caleb & Brown
        </WebsiteLink>
      </Container>
    );
  }

  return (
    <Container>
      <StyledTypography>Opening Caleb & Brown app...</StyledTypography>
      <SpinnerWrapper>
        <CircularProgress color="secondary" />
      </SpinnerWrapper>
      <Fallback>
        <StyledTypography>If the app doesn't open automatically:</StyledTypography>
        <Link href={`com.calebandbrown://verifyemail?code=${code}`}>
          Click here to open the app
        </Link>
      </Fallback>
    </Container>
  );
};

export default VerifyEmail;
